/**
 * DriverRegister - Componente de Registro de Motorista (Etapa 1)
 * Coleta: Nome, Telefone, Email e Senha
 * Design: Paleta Watch (verde gradient)
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { registerSchema, RegisterFormData } from '../schemas/registerSchema';
import { maskPhone } from '../utils/validators';
import {
    Loader2,
    User,
    Phone,
    Mail,
    Lock,
    Eye,
    EyeOff,
    CheckCircle2
} from 'lucide-react';

export function DriverRegister() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isValid }
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        mode: 'onChange'
    });

    const phoneValue = watch('phone');

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskPhone(e.target.value);
        setValue('phone', masked, { shouldValidate: true });
    };

    const onSubmit = async (data: RegisterFormData) => {
        try {
            setIsLoading(true);

            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: data.email,
                password: data.password,
                options: {
                    data: {
                        role: 'driver',
                        full_name: data.full_name,
                        phone: data.phone
                    }
                }
            });

            if (authError) {
                console.error('[DriverRegister] Erro no signUp:', authError);

                if (authError.message.includes('already registered')) {
                    toast.error('Este email já está cadastrado. Faça login ou use outro email.');
                } else {
                    toast.error(authError.message || 'Erro ao criar conta');
                }
                return;
            }

            if (!authData.user) {
                toast.error('Erro ao criar usuário');
                return;
            }

            // Verificar se a sessão foi estabelecida
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) {
                console.log('[DriverRegister] ⚠️ Sessão não estabelecida, tentando login...');
                // Fazer login explícito para garantir a sessão
                const { error: loginError } = await supabase.auth.signInWithPassword({
                    email: data.email,
                    password: data.password
                });
                if (loginError) {
                    console.error('[DriverRegister] Erro ao fazer login:', loginError);
                    toast.error('Conta criada! Faça login para continuar.');
                    navigate('/driver/login');
                    return;
                }
            }

            const { error: insertError } = await supabase.from('drivers').insert({
                id: authData.user.id,
                email: data.email.toLowerCase().trim(),
                nome_completo: data.full_name,  // Coluna existente (NOT NULL)
                tel_whatsapp: parseInt(data.phone.replace(/\D/g, ''), 10),  // bigint
                profile_status: 'pending_submission',
                status: 'active'
            });

            if (insertError) {
                console.error('[DriverRegister] Erro ao inserir driver:', {
                    code: insertError.code,
                    message: insertError.message,
                    details: insertError.details,
                    hint: insertError.hint,
                    fullError: insertError
                });
                toast.error(`Erro ao completar cadastro: ${insertError.message}`);
                return;
            }

            console.log('[DriverRegister] ✅ Conta criada com sucesso, redirecionando para onboarding...');
            toast.success('Conta criada com sucesso! Complete seu cadastro.');

            // Redirecionar imediatamente para o onboarding
            navigate('/driver/onboarding', { replace: true });

        } catch (error) {
            console.error('[DriverRegister] Erro inesperado:', error);
            toast.error('Erro inesperado. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-green-500 to-teal-600 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo Watch */}
                <div className="text-center mb-8">
                    <img
                        src="https://locaninja.com/logotipo-watch-white.png"
                        alt="Watch Logo"
                        className="h-16 mx-auto mb-6"
                    />
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Cadastro de Motorista
                    </h1>
                    <p className="text-white/80">
                        Crie sua conta para começar a transmitir
                    </p>
                </div>

                {/* Formulário */}
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="bg-white/95 backdrop-blur-lg rounded-2xl p-8 shadow-2xl"
                >
                    {/* Campo: Nome Completo */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nome Completo
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                {...register('full_name')}
                                placeholder="Digite seu nome completo"
                                className={`w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.full_name
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                            {!errors.full_name && watch('full_name')?.length >= 3 && (
                                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                            )}
                        </div>
                        {errors.full_name && (
                            <p className="text-red-500 text-sm mt-1">{errors.full_name.message}</p>
                        )}
                    </div>

                    {/* Campo: Telefone */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Celular
                        </label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="tel"
                                value={phoneValue || ''}
                                onChange={handlePhoneChange}
                                placeholder="(99) 99999-9999"
                                maxLength={15}
                                className={`w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.phone
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                            {!errors.phone && phoneValue?.length === 15 && (
                                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                            )}
                        </div>
                        {errors.phone && (
                            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                        )}
                    </div>

                    {/* Campo: Email */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                {...register('email')}
                                placeholder="seu@email.com"
                                className={`w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.email
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                            {!errors.email && watch('email')?.includes('@') && (
                                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                            )}
                        </div>
                        {errors.email && (
                            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                        )}
                    </div>

                    {/* Campo: Senha */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Senha
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                {...register('password')}
                                placeholder="Mínimo 8 caracteres"
                                className={`w-full pl-11 pr-12 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.password
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        {errors.password && (
                            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                        )}
                        {/* Requisitos da senha */}
                        <div className="mt-2 space-y-1">
                            <PasswordRequirement
                                met={watch('password')?.length >= 8}
                                text="Mínimo 8 caracteres"
                            />
                            <PasswordRequirement
                                met={/[A-Z]/.test(watch('password') || '')}
                                text="Uma letra maiúscula"
                            />
                            <PasswordRequirement
                                met={/[0-9]/.test(watch('password') || '')}
                                text="Um número"
                            />
                        </div>
                    </div>

                    {/* Botão de Submit */}
                    <button
                        type="submit"
                        disabled={isLoading || !isValid}
                        className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${isLoading || !isValid
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-green-500/30 hover:shadow-green-500/50'
                            }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Criando conta...
                            </>
                        ) : (
                            'Criar Conta'
                        )}
                    </button>

                    {/* Link para Login */}
                    <p className="text-center text-gray-600 mt-6">
                        Já tem uma conta?{' '}
                        <Link
                            to="/driver/login"
                            className="text-green-600 hover:text-green-700 font-medium transition-colors"
                        >
                            Faça login
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

// Componente auxiliar para requisitos de senha
function PasswordRequirement({ met, text }: { met: boolean; text: string }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${met ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                }`}>
                {met ? '✓' : '○'}
            </div>
            <span className={met ? 'text-green-600' : 'text-gray-400'}>{text}</span>
        </div>
    );
}

export default DriverRegister;
